import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Avatar,
  Badge,
  IconButton,
  Tooltip,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import TranslateIcon from "@mui/icons-material/Translate";
import BugReportIcon from "@mui/icons-material/BugReport";

import {
  useAppSelector,
  useAppDispatch,
  toggleTheme,
  setSidebarOpen,
} from "../../store";
import { BrandMark } from "../common";
import {
  DRAWER_WIDTH,
  NAV_ITEMS,
  LOGIN_NAV_ITEM,
  APP_SHORT_NAME,
} from "../../constants";
import { useLanguageToggle } from "../../hooks/useLanguageToggle";
import { useTranslation } from "react-i18next";

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { sidebarOpen, themeMode } = useAppSelector((state) => state.ui);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { activeCount } = useAppSelector((state) => state.instances);
  const { compactLabel, toggleLanguage } = useLanguageToggle();
  const { t } = useTranslation();

  const userInitial = user?.email?.charAt(0).toUpperCase() || "U";
  const userName = user?.email?.split("@")[0] || "User";

  // Filter nav items based on auth state
  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.requiresAuth || isAuthenticated,
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={sidebarOpen}
      onClose={() => dispatch(setSidebarOpen(false))}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          bgcolor: "background.paper",
          borderRight: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          height: 64,
          display: "flex",
          alignItems: "center",
          px: 2,
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          cursor: "pointer",
        }}
        onClick={() => navigate("/")}
      >
        <BrandMark size={36} borderRadius={1.5} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          {APP_SHORT_NAME}
        </Typography>
      </Box>
      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: "auto", py: 2, px: 1.5 }}>
        <List disablePadding>
          {visibleItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path !== "/" &&
                location.pathname.startsWith(item.path + "/"));
            const badgeCount =
              item.path === "/instances" ? activeCount || 0 : 0;

            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) dispatch(setSidebarOpen(false));
                  }}
                  selected={isActive}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": { bgcolor: "primary.dark" },
                      "& .MuiListItemIcon-root": {
                        color: "primary.contrastText",
                      },
                    },
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActive ? "inherit" : "text.secondary",
                    }}
                  >
                    {badgeCount > 0 ? (
                      <Badge
                        badgeContent={badgeCount}
                        color={isActive ? "default" : "primary"}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.65rem",
                            height: 18,
                            minWidth: 18,
                            ...(isActive && {
                              bgcolor: "primary.contrastText",
                              color: "primary.main",
                            }),
                          },
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    slotProps={{
                      primary: {
                        sx: {
                          fontWeight: isActive ? 600 : 500,
                          fontSize: "0.9rem",
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component="a"
              href="https://bugs.pinehood.tech/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: 2,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                <BugReportIcon />
              </ListItemIcon>
              <ListItemText
                primary={t("nav.reportBug")}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Show Sign In button for logged-out users */}
          {!isAuthenticated && (
            <ListItem disablePadding sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate("/login")}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  borderWidth: 2,
                  "&:hover": { borderWidth: 2 },
                }}
              >
                {LOGIN_NAV_ITEM.label}
              </Button>
            </ListItem>
          )}
        </List>
      </Box>
      {/* Bottom section */}
      <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {isAuthenticated ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1,
              cursor: "pointer",
              borderRadius: 2,
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => navigate("/settings")}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontSize: "0.875rem",
                fontWeight: 700,
              }}
            >
              {userInitial}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                {userName}
              </Typography>
            </Box>
            <Tooltip title={compactLabel}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLanguage();
                }}
                sx={{ color: "text.secondary" }}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={themeMode === "dark" ? "Light mode" : "Dark mode"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(toggleTheme());
                }}
                sx={{ color: "text.secondary" }}
              >
                {themeMode === "dark" ? (
                  <LightModeIcon fontSize="small" />
                ) : (
                  <DarkModeIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <Tooltip title={compactLabel}>
              <IconButton
                size="small"
                onClick={toggleLanguage}
                sx={{ color: "text.secondary" }}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={themeMode === "dark" ? "Light mode" : "Dark mode"}>
              <IconButton
                size="small"
                onClick={() => dispatch(toggleTheme())}
                sx={{ color: "text.secondary" }}
              >
                {themeMode === "dark" ? (
                  <LightModeIcon fontSize="small" />
                ) : (
                  <DarkModeIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
export { DRAWER_WIDTH };
